import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { mapSearchFilterToGlobalFilter } from '@components/features/filters/helpers/mapSearchFilterToGlobalFilter';
import { SEARCH_FORM_FILTERS_OBJECT } from '@components/features/filters/helpers/searchFiltersObject';
import { JobSearchFiltersHubProps } from '@type/filters';
import { useFiltersHub } from '@hooks/filters/useFiltersHub';
import { OpenSidebarButton, TopBarGroup, TopBarSingle } from '@components/features/filters/partials';
import { LocationForm } from '@components/features/filters/forms';
import TopBarStandaloneLinks from '@components/features/filters/partials/TopBarStandaloneLinks';

const sortByFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.sortBy, 'radio');
const domainsFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.domains, 'checkbox');
const careerLevelsFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.careerLevels, 'checkbox');
const employmentTypesFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.employmentTypes, 'checkbox');
const spokenLanguagesFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.spokenLanguages, 'checkbox');
const benefitsFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.benefits, 'checkbox');

/**
 * @description TopBarJobSearchFiltersHub - The hub for the top bar job search filters.
 * * **queryParams** - the query params for the filters.
 * * **setQueryParams** - the function to set the query params.
 * * **toggleDrawer** - the function to toggle the drawer.
 */
export const TopBarJobSearchFiltersHub = (props: JobSearchFiltersHubProps) => {
  // Destructure props
  const {
    queryParams,
    setQueryParams,
    setFilterLabels,
    toggleDrawer,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // FILTERS HUB HOOK
  // ***********************************************
  const {
    formRef,
    handleStep,
    record,
    submitForm,
    formStatus,
    setFormValues,
  } = useFiltersHub({ queryParams, setQueryParams, externalFieldsList: ['keyword'] });

  const { filterLabels } = formStatus;

  useEffect(() => {
    if (filterLabels && setFilterLabels) {
      const updatedFilterLabels = Object.keys(queryParams).length === 0
        ? {}
        : { ...filterLabels, ...(queryParams.keyword && { keyword: [queryParams.keyword] }) };

      setFilterLabels(updatedFilterLabels);
    }
  }, [queryParams, filterLabels, setFilterLabels]);

  // Render component
  // ***********************************************
  return (
    <form noValidate onSubmit={handleStep} ref={formRef} className="relative flex items-center gap-x-2">

      <OpenSidebarButton toggleDrawer={toggleDrawer} />

      <TopBarStandaloneLinks />

      {/* Filter: Location , we can handle to show in popover by passing state value withPopover */}
      <LocationForm
        recordFunc={record}
        submitFunc={submitForm}
        withRadiusInKm={false}
        withSubmitButton
        withSuggestedLocations
        idPrefix="topBar_"
        filterLabels={filterLabels}
        withPopover
        withUserLocation
      />

      {/* Sort Group */}
      <TopBarGroup
        data={sortByFilterObject.fields}
        fieldName="sort"
        panelHelperText={`${t('order-by')}:`}
        idPrefix="topBar_"
        filterGroupName={SEARCH_FORM_FILTERS_OBJECT.sortBy.label}
        submitFunc={submitForm}
        recordFunc={record}
        dropdownArrowPosition="right"
      />

      {/* Domains Group */}
      <TopBarGroup
        data={domainsFilterObject.fields}
        fieldName="domain"
        panelHelperText="filters.domains.popover.title"
        idPrefix="topBar_"
        multiple
        filterGroupName={SEARCH_FORM_FILTERS_OBJECT.domains.label}
        alwaysShow
        submitFunc={submitForm}
        recordFunc={record}
        recordOptions={{ convertValue: 'commaStringToArray' }}
        queryParam={queryParams?.domain?.toString()}
        initialSelectedIndex={[]}
        dropdownArrowPosition="right"
        clearGroup={queryParams?.domain?.length ? () => {
          setFormValues({ domain: '' });
        }
          : undefined}
      />

      {/* Career Levels group */}
      {queryParams?.careerLevels && (
        <TopBarGroup
          data={careerLevelsFilterObject.fields}
          fieldName="careerLevels"
          panelHelperText={SEARCH_FORM_FILTERS_OBJECT.careerLevels.label}
          idPrefix="topBar_"
          multiple
          filterGroupName={SEARCH_FORM_FILTERS_OBJECT.careerLevels.label}
          submitFunc={submitForm}
          recordFunc={record}
          recordOptions={{ convertValue: 'commaStringToArray' }}
          queryParam={queryParams?.careerLevels?.toString()}
          clearGroup={() => setFormValues({ careerLevels: '' })}
        />
      )}

      {/* Employment Types group */}
      {queryParams?.employmentTypes && (
        <TopBarGroup
          data={employmentTypesFilterObject.fields}
          fieldName="employmentTypes"
          panelHelperText={SEARCH_FORM_FILTERS_OBJECT.employmentTypes.label}
          idPrefix="topBar_"
          multiple
          filterGroupName={SEARCH_FORM_FILTERS_OBJECT.employmentTypes.label}
          submitFunc={submitForm}
          recordFunc={record}
          recordOptions={{ convertValue: 'commaStringToArray' }}
          queryParam={queryParams?.employmentTypes?.toString()}
          clearGroup={() => setFormValues({ employmentTypes: '' })}
        />
      )}

      {/* Spoken Languages group */}
      {queryParams?.spokenLanguages && (
        <TopBarGroup
          data={spokenLanguagesFilterObject.fields}
          fieldName="spokenLanguages"
          idPrefix="topBar_"
          filterGroupName={SEARCH_FORM_FILTERS_OBJECT.spokenLanguages.label}
          panelHelperText={SEARCH_FORM_FILTERS_OBJECT.spokenLanguages.label}
          multiple
          submitFunc={submitForm}
          recordFunc={record}
          recordOptions={{ convertValue: 'commaStringToArray' }}
          queryParam={queryParams.spokenLanguages.toString()}
          clearGroup={() => setFormValues({ spokenLanguages: '' })}
        />
      )}

      {/* Remote */}
      {!!queryParams?.remote && (
        <TopBarSingle
          idPrefix="topBar_"
          filterName="remote"
          filterLabel={t(SEARCH_FORM_FILTERS_OBJECT.remote.label)}
          submitFunc={submitForm}
          recordFunc={record}
          setFormValues={setFormValues}
          primaryValue={queryParams.remote}
        />
      )}

      {/* Benefits group */}
      {queryParams?.benefits && (
        <TopBarGroup
          data={benefitsFilterObject.fields}
          fieldName="benefits"
          idPrefix="topBar_"
          filterGroupName={t(SEARCH_FORM_FILTERS_OBJECT.benefits.label)}
          panelHelperText={t(SEARCH_FORM_FILTERS_OBJECT.benefits.label)}
          multiple
          clearGroup={() => setFormValues({ benefits: '' })}
          submitFunc={submitForm}
          recordFunc={record}
          recordOptions={{ convertValue: 'commaStringToArray' }}
          queryParam={queryParams.benefits.toString()}
        />
      )}

    </form>
  );
};
